import axios from 'axios';
import { apiUrl } from './config';
import { getAuthToken, getPortalType } from '@/utils/auth';

/**
 * Quests API service
 * Handles all API calls for the Quests feature with error handling and response formatting
 */

// Error handler for API requests
const handleApiError = (error) => {
  if (error.response) {
    // Server responded with error status
    const { status, data, headers } = error.response;
    
    // Check if this is an offline response from service worker
    if (status === 503 && headers?.['x-service-worker'] === 'offline') {
      return {
        success: false,
        error: 'Offline',
        status: 503,
        offline: true,
        data: data,
        message: data?.message || 'You are currently offline. Please check your internet connection.'
      };
    }
    
    // Check if response data indicates offline status
    if (data?.offline === true) {
      return {
        success: false,
        error: 'Offline',
        status: status || 503,
        offline: true,
        data: data,
        message: data?.message || 'You are currently offline. Please check your internet connection.'
      };
    }
    
    const errorMessage = data?.error || data?.message || data?.detail || `Server error (${status})`;
    
    return {
      success: false,
      error: errorMessage,
      status,
      data: data,
      message: errorMessage
    };
  } else if (error.request) {
    // Request made but no response received (likely offline)
    return {
      success: false,
      error: 'Network error: No response from server',
      offline: true,
      message: 'Unable to connect to server. Please check your internet connection.'
    };
  } else {
    // Error in request setup
    return {
      success: false,
      error: error.message || 'An unexpected error occurred',
      message: error.message || 'An unexpected error occurred'
    };
  }
};

// Get authentication headers
const getAuthHeaders = () => {
  const portal = getPortalType(window.location.pathname || '');
  const token = getAuthToken(portal);
  
  if (!token) {
    throw new Error('Authentication token not found. Please log in again.');
  }
  
  return {
    Authorization: `Bearer ${token}`,
    'Content-Type': 'application/json'
  };
};

// Format response data
const formatResponse = (data, message = null) => {
  return {
    success: true,
    data,
    message,
    error: null
  };
};

// Retry logic for failed API calls
const retryRequest = async (requestFn, maxRetries = 3, delay = 1000) => {
  let lastError;
  
  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    try {
      return await requestFn();
    } catch (error) {
      lastError = error;
      
      // Don't retry on 4xx errors (client errors)
      if (error.response && error.response.status >= 400 && error.response.status < 500) {
        throw error;
      }
      
      // Don't retry on last attempt
      if (attempt === maxRetries) {
        throw error;
      }
      
      // Wait before retrying (exponential backoff)
      const waitTime = delay * Math.pow(2, attempt - 1);
      await new Promise(resolve => setTimeout(resolve, waitTime));
      
      console.log(`Retry attempt ${attempt + 1}/${maxRetries} after ${waitTime}ms`);
    }
  }
  
  throw lastError;
};

/**
 * Generate a new quest
 * @param {number} subjectId - Subject ID
 * @param {number} chapterId - Chapter ID
 * @param {string} mode - 'practice' or 'test'
 * @param {string} questionType - Question type (written_detailed, short_answer, mcq, true_false, mixed)
 * @param {string} difficulty - Difficulty level (easy, moderate, hard, mixed)
 * @param {number} retries - Number of retries (default: 2)
 * @param {string} questionSource - Question source ('ai_generated' or 'previous_years', default: 'ai_generated')
 * @returns {Promise<Object>} Formatted response with quest data
 */
export const generateQuest = async (subjectId, chapterId, mode, questionType, difficulty, retries = 2, questionSource = 'ai_generated') => {
  try {
    // Validate parameters
    if (!subjectId || !chapterId || !mode || !questionType || !difficulty) {
      throw new Error('Missing required parameters for quest generation');
    }

    const questData = {
      subject: subjectId,
      chapter: chapterId,
      mode: mode,
      question_type: questionType,
      difficulty: difficulty,
      question_source: questionSource || 'ai_generated'
    };

    const response = await retryRequest(
      () => axios.post(
        apiUrl('v1/quests/generate/'),
        questData,
        {
          headers: getAuthHeaders(),
          timeout: 60000 // 60 seconds timeout for generation
        }
      ),
      retries
    );

    return formatResponse(response.data, 'Quest generated successfully');
  } catch (error) {
    console.error('Error generating quest:', error);
    return handleApiError(error);
  }
};

/**
 * Get quest list with filtering and pagination
 * @param {string} status - Filter by status (draft, in_progress, completed)
 * @param {number} page - Page number (default: 1)
 * @param {Object} additionalParams - Additional query parameters (mode, subject, chapter, etc.)
 * @returns {Promise<Object>} Formatted response with quest list
 */
export const getQuestList = async (status = null, page = 1, additionalParams = {}, retries = 2) => {
  try {
    const params = {
      page: page,
      page_size: 20, // Default page size
      ...additionalParams
    };

    // Add status filter if provided
    if (status) {
      params.status = status;
    }

    const response = await retryRequest(
      () => axios.get(
        apiUrl('v1/quests/'),
        {
          params,
          headers: getAuthHeaders()
        }
      ),
      retries
    );

    // Format paginated response
    const data = response.data;
    const formattedData = {
      results: data.results || data,
      count: data.count || (Array.isArray(data) ? data.length : 0),
      next: data.next || null,
      previous: data.previous || null,
      page: page,
      pageSize: params.page_size
    };

    return formatResponse(formattedData, 'Quests loaded successfully');
  } catch (error) {
    console.error('Error fetching quest list:', error);
    return handleApiError(error);
  }
};

/**
 * Get current question from quest set
 * @param {number|string} questSetId - Quest Set ID
 * @returns {Promise<Object>} Formatted response with current question and progress
 */
export const getCurrentQuestion = async (questSetId) => {
  try {
    if (!questSetId) {
      throw new Error('Quest Set ID is required');
    }

    // First get the quest to find current question number
    const questResponse = await axios.get(
      apiUrl(`v1/quests/${questSetId}/`),
      {
        headers: getAuthHeaders()
      }
    );

    const quest = questResponse.data;
    const progress = quest.progress || {};
    const currentQuestionNumber = progress.current_question_number || 1;

    // Find current question in questions array
    const questions = quest.questions || [];
    const currentQuestion = questions.find(q => q.question_number === currentQuestionNumber);

    if (!currentQuestion) {
      throw new Error('Current question not found');
    }

    // Get answer for current question if exists
    let currentAnswer = null;
    try {
      const answerResponse = await axios.get(
        apiUrl(`v1/quests/${questSetId}/answers/${currentQuestion.id}/`),
        {
          headers: getAuthHeaders()
        }
      );
      currentAnswer = answerResponse.data;
    } catch (answerError) {
      // No answer yet, that's fine
      console.log('No existing answer found for current question');
    }

    const data = {
      question: currentQuestion,
      answer: currentAnswer,
      progress: progress,
      quest: quest
    };

    return formatResponse(data, 'Current question loaded successfully');
  } catch (error) {
    console.error('Error fetching current question:', error);
    return handleApiError(error);
  }
};

/**
 * Submit answer for a question
 * @param {number|string} questSetId - Quest Set ID
 * @param {number|string} questionId - Question ID
 * @param {string} answerText - Answer text (for written/short answer questions)
 * @param {string} selectedOption - Selected option (for MCQ/True-False questions)
 * @returns {Promise<Object>} Formatted response with evaluation results
 */
export const submitAnswer = async (questSetId, questionId, answerText = null, selectedOption = null, retries = 2) => {
  try {
    if (!questSetId || !questionId) {
      throw new Error('Quest Set ID and Question ID are required');
    }

    // Validate that at least one answer is provided
    if (!answerText && !selectedOption) {
      throw new Error('Either answer text or selected option must be provided');
    }

    const answerData = {
      question: questionId,
      answer_text: answerText || '',
      selected_option: selectedOption || null
    };

    const response = await retryRequest(
      () => axios.post(
        apiUrl(`v1/quests/${questSetId}/answers/`),
        answerData,
        {
          headers: getAuthHeaders(),
          timeout: 60000 // 60 seconds timeout for AI evaluation
        }
      ),
      retries
    );

    return formatResponse(response.data, 'Answer submitted and evaluated successfully');
  } catch (error) {
    console.error('Error submitting answer:', error);
    return handleApiError(error);
  }
};

/**
 * Get results for a completed quest
 * @param {number|string} questSetId - Quest Set ID
 * @returns {Promise<Object>} Formatted response with results data
 */
export const getResults = async (questSetId) => {
  try {
    if (!questSetId) {
      throw new Error('Quest Set ID is required');
    }

    const response = await axios.get(
      apiUrl(`v1/quests/${questSetId}/results/`),
      {
        headers: getAuthHeaders()
      }
    );

    const data = response.data;
    
    // Format results data
    const formattedData = {
      quest: data.quest || null,
      review: data.review || null,
      questions: data.quest?.questions || [],
      answers: data.quest?.student_answers || []
    };

    return formatResponse(formattedData, 'Results loaded successfully');
  } catch (error) {
    console.error('Error fetching results:', error);
    return handleApiError(error);
  }
};

/**
 * Mark quest as revisited with action tracking
 * @param {number|string} questSetId - Quest Set ID
 * @param {string} action - Action type ('resumed', 'reviewed', 'continued', 'restarted')
 * @returns {Promise<Object>} Formatted response
 */
export const markQuestAsRevisited = async (questSetId, action) => {
  try {
    if (!questSetId || !action) {
      throw new Error('Quest Set ID and action are required');
    }

    // Validate action
    const validActions = ['resumed', 'reviewed', 'continued', 'restarted'];
    if (!validActions.includes(action)) {
      throw new Error(`Invalid action. Must be one of: ${validActions.join(', ')}`);
    }

    const response = await axios.post(
      apiUrl(`v1/quests/${questSetId}/revisit/`),
      { action },
      {
        headers: getAuthHeaders()
      }
    );

    return formatResponse(response.data, `Quest marked as ${action} successfully`);
  } catch (error) {
    console.error('Error marking quest as revisited:', error);
    return handleApiError(error);
  }
};

/**
 * Export questsAPI object for backward compatibility
 * This maintains the existing API structure while using new functions internally
 */
/**
 * Check if questions exist in question bank for a chapter
 * @param {number} chapterId - Chapter ID
 * @returns {Promise<Object>} Response with has_questions and count
 */
export const checkQuestionBankAvailability = async (chapterId) => {
  try {
    if (!chapterId) {
      throw new Error('Chapter ID is required');
    }

    const response = await axios.get(
      apiUrl('v1/quests/question-bank/check/'),
      {
        params: { chapter_id: chapterId },
        headers: getAuthHeaders()
      }
    );

    return formatResponse(response.data, 'Question bank availability checked');
  } catch (error) {
    console.error('Error checking question bank availability:', error);
    return handleApiError(error);
  }
};

export const questsAPI = {
  /**
   * Generate a new quest set (wrapper for generateQuest)
   */
  generate: async (questData) => {
    const result = await generateQuest(
      questData.subject,
      questData.chapter,
      questData.mode,
      questData.question_type,
      questData.difficulty,
      2, // retries
      questData.question_source // question_source parameter
    );
    if (result.success) {
      return result.data;
    }
    throw new Error(result.error || result.message);
  },

  /**
   * List all quests with optional filtering (wrapper for getQuestList)
   */
  list: async (params = {}) => {
    const { status, page, ...additionalParams } = params;
    const result = await getQuestList(status, page || 1, additionalParams);
    if (result.success) {
      // Return the full paginated payload so callers can access pagination metadata
      return result.data;
    }
    throw new Error(result.error || result.message);
  },

  /**
   * Get a single quest by ID
   */
  get: async (questId) => {
    try {
      const response = await axios.get(
        apiUrl(`v1/quests/${questId}/`),
        {
          headers: getAuthHeaders()
        }
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching quest:', error);
      const errorResult = handleApiError(error);
      throw new Error(errorResult.error || errorResult.message);
    }
  },

  /**
   * Update a quest
   */
  update: async (questId, updates) => {
    try {
      const response = await axios.patch(
        apiUrl(`v1/quests/${questId}/`),
        updates,
        {
          headers: getAuthHeaders()
        }
      );
      return response.data;
    } catch (error) {
      console.error('Error updating quest:', error);
      const errorResult = handleApiError(error);
      throw new Error(errorResult.error || errorResult.message);
    }
  },

  /**
   * Delete a quest
   */
  delete: async (questId) => {
    try {
      const response = await axios.delete(
        apiUrl(`v1/quests/${questId}/`),
        {
          headers: getAuthHeaders()
        }
      );
      return response.data;
    } catch (error) {
      console.error('Error deleting quest:', error);
      const errorResult = handleApiError(error);
      throw new Error(errorResult.error || errorResult.message);
    }
  },

  /**
   * Record revisit action (wrapper for markQuestAsRevisited)
   */
  revisit: async (questId, data) => {
    const result = await markQuestAsRevisited(questId, data.action);
    if (result.success) {
      return result.data;
    }
    throw new Error(result.error || result.message);
  },

  /**
   * Get quest results (wrapper for getResults)
   */
  getResults: async (questId) => {
    const result = await getResults(questId);
    if (result.success) {
      return result.data;
    }
    throw new Error(result.error || result.message);
  },

  /**
   * Navigate to next/previous question
   */
  navigate: async (questId, data) => {
    try {
      const response = await axios.post(
        apiUrl(`v1/quests/${questId}/navigation/`),
        data,
        {
          headers: getAuthHeaders()
        }
      );
      return response.data;
    } catch (error) {
      console.error('Error navigating:', error);
      const errorResult = handleApiError(error);
      throw new Error(errorResult.error || errorResult.message);
    }
  },

  /**
   * Submit answer for evaluation (wrapper for submitAnswer)
   */
  submitAnswer: async (questId, answerData) => {
    const result = await submitAnswer(
      questId,
      answerData.question,
      answerData.answer_text,
      answerData.selected_option
    );
    if (result.success) {
      return result.data;
    }
    throw new Error(result.error || result.message);
  },

  /**
   * Update/retry answer
   * Uses PATCH instead of PUT for better compatibility with Django REST Framework custom actions
   */
  updateAnswer: async (questId, answerId, updates) => {
    try {
      // Try PATCH first (preferred for partial updates)
      try {
        const response = await axios.patch(
          apiUrl(`v1/quests/${questId}/answers/${answerId}/`),
          updates,
          {
            headers: getAuthHeaders(),
            timeout: 60000 // 60 seconds timeout for AI evaluation
          }
        );
        return response.data;
      } catch (patchError) {
        // Fallback to PUT if PATCH fails
        if (patchError.response?.status === 405) {
          const response = await axios.put(
            apiUrl(`v1/quests/${questId}/answers/${answerId}/`),
            updates,
            {
              headers: getAuthHeaders(),
              timeout: 60000 // 60 seconds timeout for AI evaluation
            }
          );
          return response.data;
        }
        throw patchError;
      }
    } catch (error) {
      console.error('Error updating answer:', error);
      const errorResult = handleApiError(error);
      throw new Error(errorResult.error || errorResult.message);
    }
  },

  /**
   * Get correct answer for a question
   */
  getCorrectAnswer: async (questId, questionId) => {
    try {
      const response = await axios.get(
        apiUrl(`v1/quests/${questId}/answers/${questionId}/`),
        {
          headers: getAuthHeaders()
        }
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching correct answer:', error);
      const errorResult = handleApiError(error);
      throw new Error(errorResult.error || errorResult.message);
    }
  },

  /**
   * Get answer hint for a question
   */
  getAnswerHint: async (questId, questionId) => {
    try {
      const response = await axios.get(
        apiUrl(`v1/quests/${questId}/questions/${questionId}/answer-hint/`),
        {
          headers: getAuthHeaders()
        }
      );
      return formatResponse(response.data, 'Answer hint retrieved successfully');
    } catch (error) {
      console.error('Error fetching answer hint:', error);
      return handleApiError(error);
    }
  },

  /**
   * Get question hint for a question
   */
  getQuestionHint: async (questId, questionId) => {
    try {
      const response = await axios.get(
        apiUrl(`v1/quests/${questId}/questions/${questionId}/question-hint/`),
        {
          headers: getAuthHeaders()
        }
      );
      return formatResponse(response.data, 'Question hint retrieved successfully');
    } catch (error) {
      console.error('Error fetching question hint:', error);
      return handleApiError(error);
    }
  },

  /**
   * Save answer as draft (auto-save without evaluation)
   */
  saveDraftAnswer: async (questId, answerData) => {
    try {
      const response = await axios.patch(
        apiUrl(`v1/quests/${questId}/answers/draft/`),
        {
          question: answerData.question,
          answer_text: answerData.answer_text || '',
          selected_option: answerData.selected_option || null
        },
        {
          headers: getAuthHeaders(),
          timeout: 10000 // 10 seconds timeout for draft save
        }
      );
      return response.data;
    } catch (error) {
      console.error('Error saving draft answer:', error);
      // Don't throw error for draft saves - fail silently
      return null;
    }
  },

  /**
   * Save progress in bulk (all answers + elapsed time)
   */
  saveProgress: async (questId, progressData) => {
    try {
      const response = await axios.post(
        apiUrl(`v1/quests/${questId}/save-progress/`),
        {
          answers: progressData.answers || [],
          elapsed_time_seconds: progressData.elapsed_time_seconds,
          current_question_number: progressData.current_question_number
        },
        {
          headers: getAuthHeaders(),
          timeout: 30000 // 30 seconds timeout for bulk save
        }
      );
      return response.data;
    } catch (error) {
      console.error('Error saving progress:', error);
      const errorResult = handleApiError(error);
      throw new Error(errorResult.error || errorResult.message);
    }
  }
};

// Export new functions for direct use
export default {
  generateQuest,
  getQuestList,
  getCurrentQuestion,
  submitAnswer,
  getResults,
  markQuestAsRevisited,
  questsAPI // For backward compatibility
};
